<script setup>
import { formatCurrency, formatNumber, formatPercent } from '../Utils/numberFormat.js'
import { computed, onMounted, ref } from 'vue'
import axios from 'axios'
import { useToast } from 'vue-toast-notification'
import { useHttpErrorsHandler } from '../Composables/httpErrorsHandler.js'
import Loader from './Loader.vue'

const props = defineProps({
    centerId: { type: Number, required: true },
})

const $toast = useToast()
const { httpErrorsHandler } = useHttpErrorsHandler()

const year = ref(new Date().getFullYear())
const months = ref([])
// Montant saisi par mois (chaîne vide = pas d'objectif propre, le mois hérite du précédent).
const amounts = ref({})
const loading = ref(true)
// Faux tant que la première année n'est pas chargée : ensuite, un changement d'année garde le contenu affiché sous un spinner.
const initialized = ref(false)
const saving = ref(false)
const errors = ref({})

const formatAmount = (value) => formatCurrency(value, 'EUR', { maximumFractionDigits: 0 })

const monthName = (month) => {
    const label = new Intl.DateTimeFormat('fr-FR', { month: 'long' }).format(new Date(2000, month - 1, 1))
    return label.charAt(0).toUpperCase() + label.slice(1)
}

const now = new Date()
const isCurrentMonth = (month) => year.value === now.getFullYear() && month === now.getMonth() + 1
const isFutureMonth = (month) => year.value > now.getFullYear() || (year.value === now.getFullYear() && month > now.getMonth() + 1)

const progressClass = (progress) => {
    if (progress >= 100) return 'bg-success'
    if (progress >= 50) return 'bg-secondary'
    return 'bg-warning'
}

const totals = computed(() => {
    const objective = months.value.reduce((sum, row) => sum + (row.objective || 0), 0)
    const revenue = months.value.reduce((sum, row) => sum + row.revenue, 0)
    return {
        objective,
        revenue,
        progress: objective > 0 ? Math.round(revenue / objective * 1000) / 10 : null,
    }
})

// Montants tels qu'enregistrés, pour savoir s'il reste des modifications à sauvegarder.
const savedAmounts = ref({})

const applyResponse = (data) => {
    months.value = data.months
    savedAmounts.value = Object.fromEntries(data.months.map((row) => [row.month, row.inherited || row.objective === null ? '' : String(row.objective)]))
    amounts.value = { ...savedAmounts.value }
}

const normalize = (value) => (value === null || value === undefined ? '' : String(value))
const isDirty = computed(() => months.value.some((row) => normalize(amounts.value[row.month]) !== normalize(savedAmounts.value[row.month])))

const resetAmounts = () => {
    amounts.value = { ...savedAmounts.value }
    errors.value = {}
}

const fetchObjectives = () => {
    loading.value = true
    errors.value = {}
    axios.get(`/api/centers/${props.centerId}/revenue-objectives`, { params: { year: year.value } })
        .then((response) => applyResponse(response.data.data))
        .catch((error) => httpErrorsHandler(error))
        .finally(() => {
            loading.value = false
            initialized.value = true
        })
}

const changeYear = (delta) => {
    year.value += delta
    fetchObjectives()
}

const save = () => {
    saving.value = true
    errors.value = {}
    axios.put(`/api/centers/${props.centerId}/revenue-objectives`, {
        year: year.value,
        objectives: months.value.map((row) => ({
            month: row.month,
            amount: amounts.value[row.month] === '' || amounts.value[row.month] === null ? null : Number(amounts.value[row.month]),
        })),
    })
        .then((response) => {
            applyResponse(response.data.data)
            $toast.success('Objectifs enregistrés')
        })
        .catch((error) => {
            if (error.response?.status === 422) {
                errors.value = error.response.data.errors
                $toast.error('Certains montants sont invalides')
                return
            }
            httpErrorsHandler(error)
        })
        .finally(() => {
            saving.value = false
        })
}

const amountError = (index) => errors.value?.[`objectives.${index}.amount`]?.[0]

onMounted(fetchObjectives)
</script>

<template>
    <div>
        <!-- Barre d'outils : année et synthèse -->
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-4">
            <div class="d-inline-flex align-items-center gap-1 bg-light rounded-pill p-1">
                <button type="button" class="btn btn-sm btn-soft-secondary shadow-none rounded-circle btn-icon" @click="changeYear(-1)" :disabled="loading" title="Année précédente">
                    <i class="ri-arrow-left-s-line fs-16"></i>
                </button>
                <span class="fw-semibold fs-15 px-3">{{ year }}</span>
                <button type="button" class="btn btn-sm btn-soft-secondary shadow-none rounded-circle btn-icon" @click="changeYear(1)" :disabled="loading" title="Année suivante">
                    <i class="ri-arrow-right-s-line fs-16"></i>
                </button>
            </div>
            <span class="badge bg-warning-subtle text-warning fs-12" v-if="isDirty">
                <i class="ri-edit-2-line align-middle me-1"></i>Modifications non enregistrées
            </span>
        </div>

        <loader v-if="!initialized" />

        <div class="position-relative" v-else>
            <div class="objectives-loading" v-if="loading">
                <div class="spinner-border text-secondary" role="status"></div>
            </div>

            <div :class="{ 'opacity-50 pe-none': loading }">
                <div class="row g-3 mb-4">
                    <div class="col-md-4">
                        <div class="border rounded p-3 h-100">
                            <p class="text-uppercase fw-medium text-muted fs-12 mb-2">Objectif annuel</p>
                            <h4 class="mb-0 ff-secondary">{{ totals.objective > 0 ? formatAmount(totals.objective) : '—' }}</h4>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="border rounded p-3 h-100">
                            <p class="text-uppercase fw-medium text-muted fs-12 mb-2">CA réalisé</p>
                            <h4 class="mb-0 ff-secondary">{{ formatAmount(totals.revenue) }}</h4>
                        </div>
                    </div>
                    <div class="col-md-4">
                        <div class="border rounded p-3 h-100">
                            <p class="text-uppercase fw-medium text-muted fs-12 mb-2">Avancement annuel</p>
                            <template v-if="totals.progress !== null">
                                <h4 class="mb-2 ff-secondary">{{ formatPercent(totals.progress) }}</h4>
                                <div class="progress progress-sm">
                                    <div class="progress-bar" :class="progressClass(totals.progress)" role="progressbar" :style="{ width: Math.min(totals.progress, 100) + '%' }"></div>
                                </div>
                            </template>
                            <h4 class="mb-0 ff-secondary text-muted" v-else>—</h4>
                        </div>
                    </div>
                </div>

                <!-- Objectifs mensuels -->
                <div class="border rounded overflow-hidden">
                    <div class="table-responsive">
                        <table class="table table-borderless align-middle table-nowrap mb-0">
                            <thead class="table-light">
                                <tr class="text-muted">
                                    <th class="ps-3" style="width: 20%;">Mois</th>
                                    <th style="width: 28%;">Objectif</th>
                                    <th style="width: 18%;">CA réalisé</th>
                                    <th class="pe-3">Avancement</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr
                                    v-for="(row, index) in months"
                                    :key="row.month"
                                    class="border-top"
                                    :class="{ 'bg-secondary-subtle bg-opacity-50': isCurrentMonth(row.month) }"
                                >
                                    <td class="ps-3 fw-medium">
                                        {{ monthName(row.month) }}
                                        <span class="badge bg-secondary ms-1" v-if="isCurrentMonth(row.month)">En cours</span>
                                    </td>
                                    <td>
                                        <div class="input-group input-group-sm" style="max-width: 190px;">
                                            <input
                                                type="number"
                                                min="0"
                                                step="100"
                                                class="form-control"
                                                :class="{ 'is-invalid': amountError(index) }"
                                                v-model="amounts[row.month]"
                                                :placeholder="row.inherited ? `${formatNumber(row.objective)} (repris)` : 'Aucun objectif'"
                                            >
                                            <span class="input-group-text">€</span>
                                            <div class="invalid-feedback">{{ amountError(index) }}</div>
                                        </div>
                                    </td>
                                    <td class="fw-medium">
                                        <span v-if="!isFutureMonth(row.month)">{{ formatAmount(row.revenue) }}</span>
                                        <span class="text-muted fw-normal" v-else>—</span>
                                    </td>
                                    <td class="pe-3">
                                        <div class="d-flex align-items-center gap-2" v-if="row.progress !== null && !isFutureMonth(row.month)">
                                            <div class="progress progress-sm flex-grow-1" style="max-width: 220px;">
                                                <div class="progress-bar" :class="progressClass(row.progress)" role="progressbar" :style="{ width: Math.min(row.progress, 100) + '%' }"></div>
                                            </div>
                                            <span class="fs-12 fw-semibold" style="min-width: 48px;">{{ formatPercent(row.progress) }}</span>
                                        </div>
                                        <span class="text-muted" v-else>—</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>

                <!-- Pied : aide et actions -->
                <div class="d-flex flex-wrap align-items-center justify-content-between gap-3 border-top pt-3 mt-4">
                    <p class="text-muted fs-13 mb-0">
                        <i class="ri-information-line align-middle me-1"></i>
                        Un mois laissé vide reprend l'objectif du mois précédent. Le CA est mis à jour chaque nuit.
                    </p>
                    <div class="d-flex gap-2">
                        <button type="button" class="btn btn-soft-secondary shadow-none" @click="resetAmounts" :disabled="!isDirty || saving">
                            Annuler
                        </button>
                        <button type="button" class="btn btn-secondary bg-gradient" @click="save" :disabled="!isDirty || saving">
                            <span class="spinner-border spinner-border-sm me-1" v-if="saving"></span>
                            <i class="ri-save-line align-bottom me-1" v-else></i>
                            Enregistrer
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
.objectives-loading {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
